import React, { useEffect, useState } from "react";
import { Drawer, Empty, Spin, Table, Tabs, Tag, Tooltip, message } from "antd";
import { ColumnsType } from "antd/lib/table";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { fmtINR, fmtQty, num, perUnit } from "./threeMShared";
import { perUnitLine } from "./threeMCells";
import {
  headLabelOf,
  headOrderOf,
  moneyPerUnit,
} from "../../shared/useRateUnit";
import { useFontScale } from "../../../../theme/fontScale";

interface TaskMaterialLine {
  taskBoqId: number;
  materialId: number;
  materialName: string;
  materialCode?: string;
  uomName?: string;
  allottedQuantity?: string;
  rate?: string;
  totalAmount?: string;
  requestedQuantity?: string;
  orderedQuantity?: string;
  issuedQuantity?: string;
  utilizedQuantity?: string;
  status?: string;
  fromNorm?: boolean;
  normCode?: string;
  normTitle?: string;
  appliedQuantity?: string;
  appliedRate?: string;
}

interface TaskManpowerLine {
  taskResourceId: number;
  laborType?: string;
  laborCategory?: string;
  resourceCount?: number | string;
  quantity?: string;
  workUom?: string;
  rate?: string;
  totalAmount?: string;
  // PRW manpower-norm requirement lines (fromNorm=true) carry item identity
  // from manpower_norms instead of labor type/category.
  fromNorm?: boolean;
  applicationId?: number;
  itemCode?: string;
  itemDescription?: string;
  pucFactor?: string;
  uomName?: string;
  category?: string;
}

interface TaskMachineryLine {
  taskToolId: number;
  toolName?: string;
  toolType?: string;
  serialNumber?: string;
  count?: number | string;
  quantity?: string;
  workUom?: string;
  rate?: string;
  totalAmount?: string;
  status?: string;
  consumables?: Array<{
    materialId: number;
    materialName?: string;
    quantity?: string;
    rate?: string;
    totalAmount?: string;
  }>;
  // Applied machinery-norm lines (fromNorm=true) carry item identity from
  // machinery_norms instead of a tool reference.
  fromNorm?: boolean;
  applicationId?: number;
  normCode?: string;
  description?: string;
  category?: string;
  uomName?: string;
  // Applied equipment deployment-norm lines (fromEquipment=true) — a third
  // machinery source, live-derived from the deployment-norm matrix. They
  // reuse normCode/description/quantity/totalAmount and add the per-asset
  // split. Absent on older backends (all fields optional).
  fromEquipment?: boolean;
  deploymentNormId?: number;
  totalHours?: string;
  equipmentAssets?: Array<{
    assetId?: number;
    assetName?: string;
    uomName?: string;
    hours?: string;
    hourlyRate?: string;
    amount?: string;
    /** false = considered in the matrix but not deployed (0-cost row). */
    deployed?: boolean;
    /** true = no effective rate found for the asset ('no rate' flag). */
    missingRate?: boolean;
  }>;
  /**
   * EQUIPMENT lines only: the nine engine heads behind totalAmount. A head the
   * engine did not return is ABSENT from this list — never present with 0.
   * `perUnit` is deliberately not populated on this endpoint; this drawer
   * divides by the TASK work quantity, the same divisor its footer uses.
   */
  equipmentHeads?: Array<{ head: string; amount: string; perUnit?: string }>;
}

interface TaskDetail {
  taskId: number;
  taskName: string;
  materials?: TaskMaterialLine[];
  manpower?: TaskManpowerLine[];
  machinery?: TaskMachineryLine[];
  // Per-unit divisor for the tab footers (cost ÷ work quantity).
  workQuantity?: string;
  workUom?: string;
}

const qtyCell = (value: any) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(value)}</span>
);

const amountCell = (value: any) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(value)}</span>
);

// Tab footer: total of the tab's lines + per-unit (total ÷ task work quantity).
const TabTotals: React.FC<{
  label: string;
  total: number;
  workQuantity?: string;
  workUom?: string;
}> = ({ label, total, workQuantity, workUom }) => {
  const { fs } = useFontScale();
  const qty = num(workQuantity);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "baseline",
        gap: 16,
        marginTop: 10,
        paddingTop: 8,
        borderTop: "1px solid #e4e9f0",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {qty > 0 && total > 0 && (
        <span style={{ fontSize: fs(12), color: "#8094ae" }}>
          Per unit: {fmtINR(total / qty)}
          {workUom ? `/${workUom}` : "/unit"}
        </span>
      )}
      <span style={{ fontSize: fs(13), color: "#526484" }}>
        {label} Total{" "}
        <strong style={{ color: "#022c57" }}>{fmtINR(total)}</strong>
      </span>
    </div>
  );
};

const TaskDetailDrawer: React.FC<{
  open: boolean;
  taskId?: number;
  taskName?: string;
  onClose: () => void;
  // Render target override — the 3M report passes its own container so the
  // drawer stays visible while the report is in browser fullscreen (portals
  // left on document.body are hidden behind a fullscreened element).
  getContainer?: () => HTMLElement;
}> = ({ open, taskId, taskName, onClose, getContainer }) => {
  const { fs } = useFontScale();
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: fs(12),
    fontWeight: 600,
    color: "#022c57",
    margin: "4px 0 6px",
  };

  const sectionEmptyStyle: React.CSSProperties = {
    color: "#9ca3af",
    fontSize: fs(12),
    padding: "4px 0 2px",
  };

  const subTotalRowStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    gap: 20,
    marginTop: 10,
    fontSize: fs(12),
    color: "#526484",
    fontVariantNumeric: "tabular-nums",
  };

  const tabLabel = (label: string, count: number) => (
    <span>
      {label}{" "}
      <Tag
        style={{ marginRight: 0, fontSize: fs(11) }}
        color={count > 0 ? "blue" : "default"}
      >
        {count}
      </Tag>
    </span>
  );

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        setLoading(true);
        setDetail(null);
        const response = await threeMReportApis.get3MTaskDetail(taskId);
        const { status, data } = response || {};
        if (cancelled) return;
        if (status) {
          setDetail(data);
        } else {
          message.error(response?.message || "Failed to load task 3M detail");
        }
      } catch (err) {
        console.error("Error loading task 3M detail:", err);
        if (!cancelled) message.error("Failed to load task 3M detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, taskId]);

  const materials = detail?.materials || [];
  const manpower = detail?.manpower || [];
  const machinery = detail?.machinery || [];

  // Manpower is dual-source: task_resources ("Other") + applied PRW manpower
  // norms ("PRW"). Manpower Total is always PRW + Others.
  const manpowerOthers = manpower.filter((m) => !m.fromNorm);
  const manpowerPrw = manpower.filter((m) => m.fromNorm);

  // Machinery is triple-source: task_tools ("Tools") + applied machinery
  // norms ("From Norm") + applied equipment deployment norms ("From Equipment
  // Norm"). Tools = neither flag — equipment lines must NOT fall into Tools.
  // Machinery Total is always Tools + From Norm + From Equipment Norm.
  const machineryTools = machinery.filter((m) => !m.fromNorm && !m.fromEquipment);
  const machineryNorms = machinery.filter((m) => m.fromNorm);
  const machineryEquipment = machinery.filter((m) => m.fromEquipment);

  const sumAmounts = (rows: Array<{ totalAmount?: string }>) =>
    rows.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0);
  const materialTotal = sumAmounts(materials);
  const manpowerOthersTotal = sumAmounts(manpowerOthers);
  const manpowerPrwTotal = sumAmounts(manpowerPrw);
  const manpowerTotal = manpowerOthersTotal + manpowerPrwTotal;
  const machineryToolsTotal = sumAmounts(machineryTools);
  const machineryNormsTotal = sumAmounts(machineryNorms);
  const machineryEquipmentTotal = sumAmounts(machineryEquipment);
  const machineryTotal =
    machineryToolsTotal + machineryNormsTotal + machineryEquipmentTotal;

  // ── Equipment cost heads ────────────────────────────────────────────────
  // Σ the applied equipment norms' head splits into ONE task-level breakdown
  // of the Equipment subtotal, the same grain (and reading order) the task
  // card's 3M block uses. Keys the wire never sent stay out of the map
  // entirely: `undefined` means "the engine had no such head", 0 means
  // "genuinely zero", and the table must not conflate them.
  const equipmentHeadTotals = machineryEquipment.reduce<Record<string, number>>(
    (acc, line) => {
      for (const head of line.equipmentHeads || []) {
        acc[head.head] = (acc[head.head] ?? 0) + num(head.amount);
      }
      return acc;
    },
    {}
  );
  const equipmentHeadKeys = Object.keys(equipmentHeadTotals).sort(
    (a, b) => headOrderOf(a) - headOrderOf(b)
  );

  // The per-unit divisor for the WHOLE tab — task work quantity, exactly what
  // TabTotals divides by, so the head rates and the footer rate cannot drift.
  // NOTE: this is deliberately NOT the "blank on mixed matrix units" rule the
  // backend applies to its own per-unit heads. We divide by the task's own
  // quantity and label with the task's own UOM, so the label is correct by
  // construction even when a norm's matrix mixes Cum and Metre lines. Do not
  // "fix" this to match aggregateByNorm.
  const taskWorkQty = num(detail?.workQuantity);
  const canPerUnit = taskWorkQty > 0;

  // Cost from equipment with no rate version in force never reaches a head, so
  // Σ heads can fall short of the authoritative Equipment total. Surface the
  // gap as its own row rather than letting the column quietly not add up.
  // ₹1, not ₹0.01: nine values serialized at 2dp drift a few paise from
  // rounding alone, and reporting that as a data problem is pure noise.
  const equipmentHeadSum = equipmentHeadKeys.reduce(
    (s, key) => s + equipmentHeadTotals[key],
    0
  );
  const equipmentHeadResidual = machineryEquipmentTotal - equipmentHeadSum;
  const showHeadResidual =
    equipmentHeadKeys.length > 0 && Math.abs(equipmentHeadResidual) >= 1;

  // An override quantity at apply time makes a norm's applied quantity differ
  // from the task's work quantity. The rate stays meaningful (₹ per unit of
  // TASK output, matching the footer) but it is no longer the matrix rate the
  // task card shows, so say so rather than letting the two look like a bug.
  const equipmentQtyMismatch =
    canPerUnit &&
    machineryEquipment.some(
      (m) => Math.abs(num(m.quantity) - taskWorkQty) > 0.01
    );

  const equipmentHeadRows: Array<{
    key: string;
    label: string;
    amount: number;
    isTotal?: boolean;
    isResidual?: boolean;
  }> = [
    ...equipmentHeadKeys.map((key) => ({
      key,
      label: headLabelOf(key),
      amount: equipmentHeadTotals[key],
    })),
    ...(showHeadResidual
      ? [
          {
            key: "__RESIDUAL__",
            label: "Not attributed",
            amount: equipmentHeadResidual,
            isResidual: true,
          },
        ]
      : []),
    ...(equipmentHeadKeys.length > 0
      ? [
          {
            key: "__TOTAL__",
            label: "Equipment Total",
            amount: machineryEquipmentTotal,
            isTotal: true,
          },
        ]
      : []),
  ];

  // Head-level ₹/unit uses moneyPerUnit, NOT fmtINR: misc at ₹2/hr on a
  // 500 Cum/hr machine is ₹0.004/Cum, which 2dp prints as "0.00" and reads as
  // free. moneyPerUnit escalates to 4dp below ₹0.005. The From Norm / norm-row
  // sub-lines keep fmtINR via perUnitLine — a whole norm line is never
  // sub-paisa per unit, and changing fmtINR would touch every 3M tree screen.
  const equipmentHeadColumns: ColumnsType<any> = [
    {
      title: "Cost Head",
      key: "head",
      render: (_: any, record: any) =>
        record.isTotal ? (
          <span style={{ fontWeight: 600, color: "#022c57" }}>
            {record.label}
          </span>
        ) : record.isResidual ? (
          <Tooltip title="Cost from equipment with no rate version in force — not decomposable into cost heads">
            <span style={{ color: "#8094ae" }}>{record.label}</span>
          </Tooltip>
        ) : (
          <Tag color="cyan" style={{ marginRight: 0 }}>
            {record.label}
          </Tag>
        ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      width: 130,
      render: (_: any, record: any) => (
        <span
          style={{
            fontWeight: record.isTotal ? 600 : 400,
            color: record.isResidual ? "#8094ae" : undefined,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtINR(record.amount)}
        </span>
      ),
    },
    // Dropped entirely when the task has no work quantity — nine em dashes
    // would be noise, and TabTotals hides its own per-unit under exactly the
    // same condition, so the tab goes per-unit-free together.
    ...(canPerUnit
      ? ([
          {
            title: `₹/${detail?.workUom || "unit"}`,
            key: "perUnit",
            align: "right",
            width: 120,
            render: (_: any, record: any) => {
              const rate = perUnit(record.amount, taskWorkQty);
              if (rate === null)
                return <span style={{ color: "#c8cfd8" }}>—</span>;
              return (
                <span
                  style={{
                    fontWeight: record.isTotal ? 600 : 400,
                    color: record.isResidual ? "#8094ae" : undefined,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ₹{moneyPerUnit(rate)}
                </span>
              );
            },
          },
        ] as ColumnsType<any>)
      : []),
  ];

  const materialColumns: ColumnsType<TaskMaterialLine> = [
    {
      title: "Material",
      key: "material",
      width: 200,
      fixed: "left",
      render: (_: any, record) => (
        <>
          <div style={{ fontWeight: 500 }}>{record.materialName}</div>
          {record.materialCode ? (
            <div style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.materialCode}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
      render: (text: string) => text || "—",
    },
    {
      title: "Allotted Qty",
      dataIndex: "allottedQuantity",
      key: "allottedQuantity",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 110,
      align: "right",
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 130,
      align: "right",
      render: (value: any) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {fmtINR(value)}
        </span>
      ),
    },
    {
      title: "Requested",
      dataIndex: "requestedQuantity",
      key: "requestedQuantity",
      width: 100,
      align: "right",
      render: qtyCell,
    },
    {
      title: "Ordered",
      dataIndex: "orderedQuantity",
      key: "orderedQuantity",
      width: 100,
      align: "right",
      render: qtyCell,
    },
    {
      title: "Issued",
      dataIndex: "issuedQuantity",
      key: "issuedQuantity",
      width: 100,
      align: "right",
      render: qtyCell,
    },
    {
      title: "Utilized",
      dataIndex: "utilizedQuantity",
      key: "utilizedQuantity",
      width: 100,
      align: "right",
      render: qtyCell,
    },
    {
      title: "Norm",
      key: "norm",
      width: 120,
      render: (_: any, record) =>
        record.fromNorm ? (
          <Tooltip
            title={
              <div style={{ fontSize: fs(12), lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600 }}>
                  {record.normTitle || record.normCode}
                </div>
                <div>Applied qty: {fmtQty(record.appliedQuantity)}</div>
                <div>Applied rate: {fmtINR(record.appliedRate)}</div>
              </div>
            }
          >
            <Tag color="purple" style={{ cursor: "help", marginRight: 0 }}>
              {record.normCode || "Norm"}
            </Tag>
          </Tooltip>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
  ];

  const manpowerColumns: ColumnsType<TaskManpowerLine> = [
    {
      title: "Labor Type",
      dataIndex: "laborType",
      key: "laborType",
      render: (text: string) => text || "—",
    },
    {
      title: "Category",
      dataIndex: "laborCategory",
      key: "laborCategory",
      render: (text: string) => text || "—",
    },
    {
      title: "Count",
      dataIndex: "resourceCount",
      key: "resourceCount",
      align: "right",
      width: 80,
      render: (value: any) => num(value),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 100,
      render: qtyCell,
    },
    {
      title: "UOM",
      dataIndex: "workUom",
      key: "workUom",
      width: 80,
      render: (text: string) => text || "—",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right",
      width: 110,
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 130,
      render: (value: any) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {fmtINR(value)}
        </span>
      ),
    },
  ];

  const manpowerPrwColumns: ColumnsType<TaskManpowerLine> = [
    {
      title: "Item",
      key: "item",
      fixed: "left",
      render: (_: any, record) => (
        <>
          <div style={{ fontWeight: 500 }}>{record.itemCode || "—"}</div>
          {record.itemDescription ? (
            <div style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.itemDescription}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: (
        <Tooltip title="Per-unit crew factor (quantity per unit of task work quantity)">
          <span style={{ cursor: "help" }}>PUC</span>
        </Tooltip>
      ),
      key: "pucFactor",
      align: "right",
      width: 90,
      render: (_: any, record) =>
        record.pucFactor ? qtyCell(record.pucFactor) : "—",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 100,
      render: qtyCell,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
      render: (text: string) => text || "—",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right",
      width: 110,
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 130,
      render: (value: any) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {fmtINR(value)}
        </span>
      ),
    },
  ];

  const machineryColumns: ColumnsType<TaskMachineryLine> = [
    {
      title: "Tool",
      dataIndex: "toolName",
      key: "toolName",
      render: (text: string) => text || "—",
    },
    {
      title: "Type",
      dataIndex: "toolType",
      key: "toolType",
      width: 110,
      render: (text: string) => text || "—",
    },
    {
      title: "Serial",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 110,
      render: (text: string) => text || "—",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "right",
      width: 70,
      render: (value: any) => num(value),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 90,
      render: qtyCell,
    },
    {
      title: "UOM",
      dataIndex: "workUom",
      key: "workUom",
      width: 70,
      render: (text: string) => text || "—",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right",
      width: 100,
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 120,
      render: (value: any) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {fmtINR(value)}
        </span>
      ),
    },
  ];

  const machineryNormColumns: ColumnsType<TaskMachineryLine> = [
    {
      title: "Norm",
      key: "norm",
      fixed: "left",
      render: (_: any, record) => (
        <>
          <div style={{ fontWeight: 500 }}>{record.normCode || "—"}</div>
          {record.description ? (
            <div style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.description}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: "Head",
      key: "category",
      width: 110,
      render: (_: any, record) =>
        record.category ? (
          <Tag color="purple" style={{ marginRight: 0 }}>
            {record.category}
          </Tag>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 100,
      render: qtyCell,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
      render: (text: string) => text || "—",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right",
      width: 110,
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 130,
      // ₹/unit sub-line on the TASK basis — the same divisor as the tab
      // footer, so the norm rows and the footer read off one basis. Note this
      // can differ from the norm's own `rate` column above when the applied
      // quantity was overridden at apply time.
      render: (value: any) => (
        <>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {fmtINR(value)}
          </span>
          {perUnitLine(detail || {}, num(value), fs)}
        </>
      ),
    },
  ];

  const machineryEquipmentColumns: ColumnsType<TaskMachineryLine> = [
    {
      title: "Norm",
      key: "norm",
      fixed: "left",
      render: (_: any, record) => (
        <>
          <div style={{ fontWeight: 500 }}>{record.normCode || "—"}</div>
          {record.description ? (
            <div style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.description}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 100,
      render: qtyCell,
    },
    {
      title: "Total Hours",
      dataIndex: "totalHours",
      key: "totalHours",
      align: "right",
      width: 110,
      render: qtyCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 130,
      render: (value: any) => (
        <>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {fmtINR(value)}
          </span>
          {perUnitLine(detail || {}, num(value), fs)}
        </>
      ),
    },
  ];

  // Per-asset split under one equipment-norm line. Rows with deployed=false
  // were considered in the matrix but not deployed — rendered muted at 0
  // cost; missingRate=true means no effective hourly rate was found.
  const equipmentAssetColumns: ColumnsType<any> = [
    {
      title: "Asset",
      key: "asset",
      render: (_: any, record: any) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 500 }}>
            {record.assetName || (record.assetId ? `Asset #${record.assetId}` : "—")}
          </span>
          {record.deployed === false ? (
            <Tooltip title="Considered in the deployment matrix but not deployed — contributes no cost">
              <Tag style={{ marginRight: 0, fontSize: fs(10) }}>
                not deployed
              </Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
      render: (text: string) => text || "—",
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      align: "right",
      width: 90,
      render: qtyCell,
    },
    {
      title: "Rate/hr",
      key: "hourlyRate",
      align: "right",
      width: 110,
      render: (_: any, record: any) =>
        record.missingRate ? (
          <Tooltip title="No effective hourly rate found for this asset">
            <Tag color="red" style={{ marginRight: 0, fontSize: fs(10) }}>
              no rate
            </Tag>
          </Tooltip>
        ) : (
          amountCell(record.hourlyRate)
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 120,
      render: amountCell,
    },
  ];

  const consumableColumns: ColumnsType<any> = [
    {
      title: "Consumable",
      dataIndex: "materialName",
      key: "materialName",
      render: (text: string, record: any) =>
        text || `Material #${record?.materialId}`,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      width: 100,
      render: qtyCell,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      align: "right",
      width: 110,
      render: amountCell,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 130,
      render: amountCell,
    },
  ];

  return (
    <Drawer
      title={detail?.taskName || taskName || "Task 3M Detail"}
      placement="right"
      width="min(960px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      {...(getContainer ? { getContainer } : {})}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : (
        <Tabs
          defaultActiveKey="material"
          items={[
            {
              key: "material",
              label: tabLabel("Material", materials.length),
              children:
                materials.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No material allocations yet"
                  />
                ) : (
                  <>
                    <Table
                      className="customTable"
                      rowKey="taskBoqId"
                      columns={materialColumns}
                      dataSource={materials}
                      pagination={false}
                      bordered
                      size="small"
                      scroll={{ x: 1150 }}
                    />
                    <TabTotals
                      label="Material"
                      total={materialTotal}
                      workQuantity={detail?.workQuantity}
                      workUom={detail?.workUom}
                    />
                  </>
                ),
            },
            {
              key: "manpower",
              label: tabLabel("Manpower", manpower.length),
              children:
                manpower.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No manpower allocations yet"
                  />
                ) : (
                  <>
                    <div style={sectionLabelStyle}>Other</div>
                    {manpowerOthers.length === 0 ? (
                      <div style={sectionEmptyStyle}>
                        No 'Others' Norm applied
                      </div>
                    ) : (
                      <Table
                        className="customTable"
                        rowKey="taskResourceId"
                        columns={manpowerColumns}
                        dataSource={manpowerOthers}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 700 }}
                      />
                    )}
                    <div style={{ ...sectionLabelStyle, marginTop: 14 }}>
                      PRW
                    </div>
                    {manpowerPrw.length === 0 ? (
                      <div style={sectionEmptyStyle}>No PRW Norm applied</div>
                    ) : (
                      <Table
                        className="customTable"
                        rowKey="applicationId"
                        columns={manpowerPrwColumns}
                        dataSource={manpowerPrw}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 700 }}
                      />
                    )}
                    <div style={subTotalRowStyle}>
                      <span>
                        Others Total{" "}
                        <strong>{fmtINR(manpowerOthersTotal)}</strong>
                      </span>
                      <span>
                        PRW Total <strong>{fmtINR(manpowerPrwTotal)}</strong>
                      </span>
                    </div>
                    <TabTotals
                      label="Manpower"
                      total={manpowerTotal}
                      workQuantity={detail?.workQuantity}
                      workUom={detail?.workUom}
                    />
                  </>
                ),
            },
            {
              key: "machinery",
              label: tabLabel("Machinery", machinery.length),
              children:
                machinery.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No machinery allocations yet"
                  />
                ) : (
                  <>
                    <div style={sectionLabelStyle}>Tools</div>
                    {machineryTools.length === 0 ? (
                      <div style={sectionEmptyStyle}>No tools allocated</div>
                    ) : (
                      <Table
                        className="customTable"
                        rowKey="taskToolId"
                        columns={machineryColumns}
                        dataSource={machineryTools}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 800 }}
                        expandable={{
                          rowExpandable: (record) =>
                            (record.consumables || []).length > 0,
                          expandedRowRender: (record) => (
                            <Table
                              className="customTable"
                              rowKey="materialId"
                              columns={consumableColumns}
                              dataSource={record.consumables || []}
                              pagination={false}
                              bordered
                              size="small"
                            />
                          ),
                        }}
                      />
                    )}
                    <div style={{ ...sectionLabelStyle, marginTop: 14 }}>
                      From Norm
                    </div>
                    {machineryNorms.length === 0 ? (
                      <div style={sectionEmptyStyle}>No Norm applied</div>
                    ) : (
                      <Table
                        className="customTable"
                        rowKey="applicationId"
                        columns={machineryNormColumns}
                        dataSource={machineryNorms}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 700 }}
                      />
                    )}
                    {/* Only when equipment lines exist — an older backend
                        never sends them and the tab stays exactly as today. */}
                    {machineryEquipment.length > 0 ? (
                      <>
                        <div style={{ ...sectionLabelStyle, marginTop: 14 }}>
                          From Equipment Norm
                        </div>
                        <Table
                          className="customTable"
                          rowKey={(record: TaskMachineryLine) =>
                            `EQ-${record.deploymentNormId ?? 0}-${
                              record.applicationId ?? record.normCode ?? ""
                            }`
                          }
                          columns={machineryEquipmentColumns}
                          dataSource={machineryEquipment}
                          pagination={false}
                          bordered
                          size="small"
                          scroll={{ x: 700 }}
                          expandable={{
                            rowExpandable: (record) =>
                              (record.equipmentAssets || []).length > 0,
                            expandedRowRender: (record) => (
                              <Table
                                className="customTable"
                                rowKey={(asset: any, index?: number) =>
                                  `${asset.assetId ?? 0}-${index ?? 0}`
                                }
                                columns={equipmentAssetColumns}
                                dataSource={record.equipmentAssets || []}
                                pagination={false}
                                bordered
                                size="small"
                                onRow={(asset: any) =>
                                  asset.deployed === false
                                    ? { style: { opacity: 0.55 } }
                                    : {}
                                }
                              />
                            ),
                          }}
                        />
                        {/* Cost heads sit in their OWN table rather than in the
                            one above: a head has no Qty and no Total Hours, and
                            the equipment table's expanded-row slot is already
                            the per-asset split, which must not be displaced. */}
                        {equipmentHeadRows.length > 0 ? (
                          <>
                            <div
                              style={{
                                ...sectionLabelStyle,
                                marginTop: 14,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span>Equipment cost heads</span>
                              {equipmentQtyMismatch ? (
                                <Tooltip
                                  title={`An applied quantity differs from the task work quantity (${fmtQty(
                                    detail?.workQuantity
                                  )} ${
                                    detail?.workUom || ""
                                  }). Per-unit figures here are on the TASK basis, matching the tab footer — the task card prices per APPLIED quantity, so the two can differ.`}
                                >
                                  <Tag
                                    color="blue"
                                    style={{ marginRight: 0, fontSize: fs(10) }}
                                  >
                                    task basis
                                  </Tag>
                                </Tooltip>
                              ) : null}
                            </div>
                            <Table
                              className="customTable"
                              rowKey="key"
                              columns={equipmentHeadColumns}
                              dataSource={equipmentHeadRows}
                              pagination={false}
                              bordered
                              size="small"
                              scroll={{ x: 480 }}
                            />
                            <div
                              style={{
                                fontSize: fs(11),
                                color: "#8094ae",
                                marginTop: 4,
                              }}
                            >
                              {canPerUnit
                                ? `Per unit = amount ÷ task work quantity (${fmtQty(
                                    detail?.workQuantity
                                  )}${
                                    detail?.workUom ? ` ${detail.workUom}` : ""
                                  }) — the same basis as the tab footer.`
                                : "No task work quantity — per-unit rates unavailable."}
                            </div>
                          </>
                        ) : null}
                      </>
                    ) : null}
                    <div style={subTotalRowStyle}>
                      <span>
                        Tools Total{" "}
                        <strong>{fmtINR(machineryToolsTotal)}</strong>
                      </span>
                      <span>
                        From Norm Total{" "}
                        <strong>{fmtINR(machineryNormsTotal)}</strong>
                      </span>
                      {machineryEquipment.length > 0 ? (
                        <span>
                          Equipment Total{" "}
                          <strong>{fmtINR(machineryEquipmentTotal)}</strong>
                        </span>
                      ) : null}
                    </div>
                    <TabTotals
                      label="Machinery"
                      total={machineryTotal}
                      workQuantity={detail?.workQuantity}
                      workUom={detail?.workUom}
                    />
                  </>
                ),
            },
          ]}
        />
      )}
    </Drawer>
  );
};

export default TaskDetailDrawer;
